import { Helmet } from "react-helmet-async";
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { get, post } from "src/utils/networkUtils";
import { getUserPermissions } from "src/utils/permissionsUtils";
import UserRewardFilters, {
  EMPTY_FILTERS,
} from "src/components/user-rewards/UserRewardFilters";
import UserRewardsHistoryTable from "src/components/user-rewards/UserRewardsHistoryTable";

// The API expects numbers or null for the balance bounds; the inputs hold
// strings so that an empty field means "no bound" rather than zero.
const toApiFilters = (filters) => ({
  ...filters,
  coinBalance: {
    ...filters.coinBalance,
    min: filters.coinBalance.min === "" ? null : Number(filters.coinBalance.min),
    max: filters.coinBalance.max === "" ? null : Number(filters.coinBalance.max),
  },
});

export default function UserRewardsPage() {
  const { isViewer } = getUserPermissions();
  const [tab, setTab] = useState("send");

  const [name, setName] = useState("");
  const [transactionName, setTransactionName] = useState("");
  const [amount, setAmount] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [userCount, setUserCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // An enabled level filter with nothing ticked matches nobody, so treat it as
  // incomplete input rather than sending a request that returns zero.
  const levelsInvalid =
    filters.levels.enabled && filters.levels.values.length === 0;

  useEffect(() => {
    if (levelsInvalid) {
      setUserCount(0);
      return undefined;
    }
    let cancelled = false;
    setCountLoading(true);
    const timer = setTimeout(() => {
      post(`/admin/user-rewards/preview`, { filters: toApiFilters(filters) })
        .then((response) => {
          if (!cancelled) setUserCount(response.userCount);
        })
        .catch((error) => {
          console.error("Error previewing reward audience:", error);
          if (!cancelled) setUserCount(null);
        })
        .finally(() => {
          if (!cancelled) setCountLoading(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [filters, levelsInvalid]);

  const loadHistory = useCallback(() => {
    setHistoryLoading(true);
    get(`/admin/user-rewards?offset=0&limit=50`)
      .then((response) => setHistory(response.rewards))
      .catch((error) => console.error("Error loading reward history:", error))
      .finally(() => setHistoryLoading(false));
  }, []);

  useEffect(() => {
    if (tab === "history" && history === null) {
      loadHistory();
    }
  }, [tab, history, loadHistory]);

  const canSend =
    !isViewer &&
    !levelsInvalid &&
    name.trim() !== "" &&
    transactionName.trim() !== "" &&
    amount !== "" &&
    Number(amount) !== 0 &&
    userCount > 0;

  const submitReward = () => {
    setSending(true);
    post(`/admin/user-rewards`, {
      name: name.trim(),
      transactionName: transactionName.trim(),
      amount: Number(amount),
      filters: toApiFilters(filters),
    })
      .then((response) => {
        setResult({
          severity: "success",
          message: `Rewarded ${response.userCount?.toLocaleString()} users with ${Number(
            amount
          ).toLocaleString()} coins.`,
        });
        setName("");
        setTransactionName("");
        setAmount("");
        setFilters(EMPTY_FILTERS);
        setHistory(null);
      })
      .catch((error) => {
        setResult({
          severity: "error",
          message:
            error.response?.data?.error ??
            error.message ??
            "Failed to send reward",
        });
      })
      .finally(() => {
        setSending(false);
        setConfirmOpen(false);
      });
  };

  return (
    <>
      <Helmet>
        <title> User Rewards </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" mb={3}>
          <Typography variant="h4" gutterBottom>
            User Rewards
          </Typography>
        </Stack>

        <Card sx={{ mb: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
            <Tabs value={tab} onChange={(event, value) => setTab(value)}>
              <Tab label="Send Reward" value="send" />
              <Tab label="History" value="history" />
            </Tabs>
          </Box>
        </Card>

        {tab === "send" ? (
          <>
            {result && (
              <Alert
                severity={result.severity}
                sx={{ mb: 3 }}
                onClose={() => setResult(null)}
              >
                {result.message}
              </Alert>
            )}

            <Card sx={{ mb: 3, p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Reward Name"
                    fullWidth
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    helperText="Shown in the History tab"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Transaction Name"
                    fullWidth
                    value={transactionName}
                    onChange={(event) => setTransactionName(event.target.value)}
                    helperText="Appears on each user's coin transaction"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Amount"
                    type="number"
                    fullWidth
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    helperText="Coins per user (negative to deduct)"
                  />
                </Grid>
              </Grid>
            </Card>

            <UserRewardFilters
              filters={filters}
              onChange={setFilters}
              disabled={sending}
            />

            <Card sx={{ mt: 3, p: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                    Matching users
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h4">
                      {countLoading
                        ? "-"
                        : userCount === null
                        ? "n/a"
                        : userCount.toLocaleString()}
                    </Typography>
                    {countLoading && <CircularProgress size={18} thickness={6} />}
                  </Stack>
                  {levelsInvalid && (
                    <Typography variant="caption" color="error">
                      Select at least one user level, or switch that filter off.
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  disabled={!canSend || sending}
                  onClick={() => setConfirmOpen(true)}
                >
                  {sending ? "Sending..." : "Send Reward"}
                </Button>
              </Stack>
            </Card>
          </>
        ) : (
          <Card>
            {historyLoading || history === null ? (
              <Box
                sx={{ display: "flex", justifyContent: "center", p: 3 }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <UserRewardsHistoryTable rows={history} />
            )}
          </Card>
        )}
      </Container>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Send this reward?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will give <b>{Number(amount || 0).toLocaleString()}</b> coins to{" "}
            <b>{(userCount ?? 0).toLocaleString()}</b> users, referenced as
            &quot;{transactionName}&quot;. Coin transactions cannot be undone
            from this page.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={submitReward} variant="contained" disabled={sending}>
            {sending ? "Sending..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
